import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatError } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DEFAULT_STATE, emptyAddress, fromLegacy, isStructured, problems as addrProblems } from "@/lib/address";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { contactEmail, missingContact } from "@/lib/contact";
import { Plus, Trash2, MapPin } from "lucide-react";

// One marker, so a required field is never marked two different ways on the same form.
// The codes this service actually sees, offered as suggestions rather than a closed list: a
// `datalist` keeps any other country typeable, which a <select> of twelve would not.
const DEFAULT_CODE = "+91";
const COMMON_CODES = ["+91", "+971", "+966", "+974", "+965", "+968", "+973",
                      "+65", "+60", "+1", "+44", "+61"];

const Req = () => <span className="text-[#F27373]" aria-hidden title="Required">*</span>;

export default function CustomerProfile() {
  const { user, refresh } = useAuth();
  const [form, setForm] = useState({ name: "", contact_email: "", mobile: "", country_code: DEFAULT_CODE, addresses: [] });
  const [busy, setBusy] = useState(false);
  // Saved addresses arrive in either shape. An entry still in the old free-text form is moved into
  // the fields for editing (`fromLegacy`) rather than shown as a block the customer cannot complete
  // — the server accepts it as-is, but the invoice can only print a city and PIN it was given.
  useEffect(() => {
    if (!user) return;
    const stored = user.addresses || (user.postal_address ? [{ label: "Home", address: user.postal_address }] : []);
    setForm({
      // Pre-filled with the provider's address when there is one; the server stores it back as
      // "follow the provider" rather than as a literal, so a change at Google still arrives.
      name: user.name || "", contact_email: contactEmail(user), mobile: user.mobile || "", country_code: user.country_code || DEFAULT_CODE,
      addresses: stored.map(a => (isStructured(a) ? { ...emptyAddress(), ...a } : fromLegacy(a))),
    });
  }, [user]);
  // Mirrors the server rules on `PUT /auth/profile`: name, email and mobile cannot be blank, the number is
  // digits only, and length is bounded by ITU-T E.164 rather than by any one country's plan —
  // there is deliberately no fixed national length, because this service is used from abroad and
  // ten digits is an Indian rule, not a universal one.
  const codeDigits = form.country_code.replace(/\D/g, "");
  const tooLong = form.mobile.length > 14 || codeDigits.length + form.mobile.length > 15;
  const tooShort = form.mobile.length > 0 && form.mobile.length < 6;
  const missing = missingContact(form);
  const badEmail = !!form.contact_email.trim() && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.contact_email.trim());
  // The customer arrived here because the layout would not let them past without these. Saying
  // so — and naming which — is the difference between a form and a dead end.
  const sentHereFor = user ? missingContact(user) : [];

  // Every saved address has to be complete, and the message names which one — "Address 2 needs a
  // PIN code" is actionable, "Update failed" is not. Mirrors `core.address.problems`.
  const addrFaults = form.addresses
    .map((a, i) => ({ name: (a.label || "").trim() || `Address ${i + 1}`, faults: addrProblems(a) }))
    .filter(x => x.faults.length);

  const save = async () => {
    if (missing.length) return toast.error(`Please add your ${missing.join(" and ")}.`);
    if (badEmail) return toast.error("That email address does not look right.");
    if (addrFaults.length) {
      const f = addrFaults[0];
      return toast.error(`${f.name} ${f.faults.join(" and ")}.`);
    }
    setBusy(true);
    try { await api.put("/auth/profile", form); await refresh(); toast.success("Profile updated"); }
    catch (e) { toast.error(formatError(e?.response?.data?.detail) || "Update failed"); }
    finally { setBusy(false); }
  };
  const addAddr = () => { if (form.addresses.length >= 5) return toast.error("Max 5 addresses"); setForm({...form, addresses:[...form.addresses, emptyAddress()]}); };
  const updAddr = (i, k, v) => { const a=[...form.addresses]; a[i]={...a[i],[k]:v}; setForm({...form, addresses:a}); };
  const delAddr = (i) => setForm({...form, addresses: form.addresses.filter((_,idx)=>idx!==i)});

  return (
    <div className="space-y-6" data-testid="profile-page">
      <div>
        <div className="text-[10px] tracking-[0.32em] uppercase text-[#CBA96C]">My Profile</div>
        <h1 className="font-serif-display text-4xl text-white mt-1">Personal details</h1>
      </div>
      {sentHereFor.length > 0 && (
        <div data-testid="profile-mobile-required" role="alert"
             className="rounded-xl border border-[#F5A524]/40 bg-[#F5A524]/10 px-4 py-3 text-sm text-[#F5A524] leading-relaxed">
          <strong className="text-white/90">Add your {sentHereFor.join(", ").replace(/, ([^,]*)$/, " and $1")} to continue.</strong>{" "}
          We use them for your invoice and to reach you about your case, and your mobile number
          unlocks the documents you download — so the rest of your workspace stays here until they
          are saved.
        </div>
      )}
      <Card className="border-white/10 rounded-2xl">
        <CardHeader><CardTitle className="font-serif-display">Contact</CardTitle></CardHeader>
        <CardContent className="space-y-4 max-w-lg">
          <div><Label>Full name <Req/></Label><Input data-testid="profile-name" value={form.name} onChange={e=>setForm({...form,name:e.target.value})} className="mt-1.5"/></div>
          {/* Editable: it is where invoices and messages go, and a provider may not have shared
              one at all. What the customer types is a contact address only — sign-in never matches
              on it (see resolve_social_user), so the account stays tied to Google/Facebook. */}
          <div>
            <Label>Email <Req/></Label>
            <Input data-testid="profile-email" type="email" autoComplete="email" value={form.contact_email}
                   onChange={e => setForm({ ...form, contact_email: e.target.value })} className="mt-1.5"/>
            <div className="text-[11px] text-[#6B7280] mt-1.5 leading-relaxed">
              {user?.email
                ? <>We send your invoice and updates here. You sign in with {user.provider === "facebook" ? "Facebook" : "Google"} as {user.email}.</>
                : <>We send your invoice and updates here. Your sign-in account did not share an email, so please add one.</>}
            </div>
            {badEmail && <div data-testid="profile-email-invalid" className="text-[11px] text-[#F5A524] mt-1">That email address does not look right.</div>}
          </div>
          <div>
            <Label>Mobile <Req/></Label>
            <div className="mt-1.5 flex gap-2">
              {/* Separate field, and the number beside it stays the national number alone — that
                  value is the password that opens the customer's documents, and every document
                  already issued says "no country code" inside it. See backend/core/phone.py. */}
              <Input data-testid="profile-country-code" list="dial-codes" value={form.country_code}
                     onChange={e => setForm({ ...form, country_code: e.target.value })}
                     aria-label="Country code" className="w-24 shrink-0" placeholder={DEFAULT_CODE}/>
              <datalist id="dial-codes">{COMMON_CODES.map(c => <option key={c} value={c}/>)}</datalist>
              <Input data-testid="profile-mobile" value={form.mobile} inputMode="numeric"
                     autoComplete="tel-national"
                     /* Non-digits are dropped as they are typed rather than refused on save: a
                        pasted "+91 98765 43210" would otherwise fold the code into the number and
                        silently change the document password. */
                     onChange={e => setForm({ ...form, mobile: e.target.value.replace(/\D/g, "") })}
                     className="flex-1"/>
            </div>
            {/* Says nothing about this number being the document password. That belongs at the
                moment of download — the toast in `DocumentPasswordNotice` and the line printed
                inside the file itself — not on a settings page the customer is filling in for a
                different reason. */}
            <div className="text-[11px] text-[#6B7280] mt-1.5 leading-relaxed">
              Digits only, without the country code. Ordering from outside India? Set the country
              code on the left and we can reach you on WhatsApp there too.
            </div>
            {tooShort && <div data-testid="profile-mobile-short" className="text-[11px] text-[#F5A524] mt-1">That looks too short — at least 6 digits.</div>}
            {tooLong && <div data-testid="profile-mobile-long" className="text-[11px] text-[#F5A524] mt-1">Country code and number together cannot exceed 15 digits.</div>}
          </div>
        </CardContent>
      </Card>

      <Card className="border-white/10 rounded-2xl">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="font-serif-display flex items-center gap-2"><MapPin className="w-4 h-4 text-[#CBA96C]"/> Delivery addresses <span className="text-xs text-[#6B7280] font-normal">({form.addresses.length}/5)</span></CardTitle>
          <Button data-testid="add-address" variant="outline" size="sm" onClick={addAddr} disabled={form.addresses.length>=5}><Plus className="w-3.5 h-3.5 mr-1"/>Add</Button>
        </CardHeader>
        <CardContent className="space-y-4">
          {form.addresses.length===0 && <div className="text-sm text-[#6B7280]">No addresses saved. Add at least one for doorstep delivery.</div>}
          {/* Separate fields rather than one block, because the invoice draws BILL TO in a fixed
              column with no wrapping of its own: a single long line was cut at the column edge and
              the city, state and PIN never printed. Fields know where the line breaks go — see
              backend/core/address.py. Label sits above, as before: side by side it ate a fixed
              140px of the row and squeezed the field that actually needs the width. */}
          {form.addresses.map((a,i)=>(
            <div key={i} data-testid={`address-block-${i}`} className="p-4 bg-white/5 border border-white/10 rounded-lg space-y-3">
              <div className="flex items-center gap-3">
                <Input placeholder="Label (Home / Office)" data-testid={`addr-label-${i}`} className="max-w-[240px]"
                  value={a.label||""} onChange={e=>updAddr(i,"label",e.target.value)}/>
                <Button variant="ghost" size="sm" onClick={()=>delAddr(i)} data-testid={`addr-del-${i}`}
                  className="text-[#F27373] ml-auto shrink-0"><Trash2 className="w-3.5 h-3.5 mr-1"/>Remove</Button>
              </div>
              <div className="space-y-3">
                <div className="space-y-1.5">
                  <Label className="text-xs text-[#9CA3AF]">Address line 1 <Req/></Label>
                  <Input placeholder="Flat / Door No, Building, Street" data-testid={`addr-line1-${i}`}
                    value={a.address1||""} onChange={e=>updAddr(i,"address1",e.target.value)}/>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-[#9CA3AF]">Address line 2</Label>
                  <Input placeholder="Area, Landmark (optional)" data-testid={`addr-line2-${i}`}
                    value={a.address2||""} onChange={e=>updAddr(i,"address2",e.target.value)}/>
                </div>
                {/* City, state and PIN on one row: three short values that belong together, and the
                    last line of a written Indian address is "Tamil Nadu - 600040". */}
                <div className="grid gap-3 sm:grid-cols-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs text-[#9CA3AF]">City <Req/></Label>
                    <Input placeholder="Chennai" data-testid={`addr-city-${i}`}
                      value={a.city||""} onChange={e=>updAddr(i,"city",e.target.value)}/>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs text-[#9CA3AF]">State <Req/></Label>
                    <Input placeholder={DEFAULT_STATE} data-testid={`addr-state-${i}`}
                      value={a.state||""} onChange={e=>updAddr(i,"state",e.target.value)}/>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs text-[#9CA3AF]">PIN code <Req/></Label>
                    {/* Digits only, six of them. inputMode brings up a numeric keypad on a phone,
                        which is how most customers fill this in. */}
                    <Input placeholder="600040" data-testid={`addr-pin-${i}`} inputMode="numeric" maxLength={6}
                      value={a.pincode||""}
                      onChange={e=>updAddr(i,"pincode",e.target.value.replace(/\D/g,"").slice(0,6))}/>
                  </div>
                </div>
              </div>
            </div>
          ))}
          {addrFaults.length > 0 && (
            <div data-testid="addr-incomplete" role="alert" className="text-xs text-[#F5A524] leading-relaxed">
              {addrFaults[0].name} {addrFaults[0].faults.join(" and ")} — a courier cannot deliver
              without it, and it prints on your invoice.
            </div>
          )}
        </CardContent>
      </Card>

      <Button data-testid="profile-save" disabled={busy || missing.length > 0 || badEmail || tooShort || tooLong || addrFaults.length > 0} onClick={save} className="bg-[#CBA96C] text-[#080C16] hover:bg-[#DBC190] rounded-full px-8 h-11">{busy?"Saving...":"Save Profile"}</Button>
    </div>
  );
}
